// Copyright (c) 2025 WiseTime. All rights reserved.

import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../../app/store"
import {
  DisableTimesheetFieldOptionsRequest,
  EnableTimesheetFieldOptionsRequest,
  ListTimesheetFieldOptionsResponse,
  TimesheetField,
  TimesheetFieldConfig,
  TimesheetFieldOption,
  TimesheetFieldSource,
} from "../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { naturalOrderCollator, notUndefined } from "../../lib/utils"

export type TimesheetFieldConfigsState = {
  [configHash: string]: TimesheetFieldConfig.AsObject,
}

export type TimesheetFieldOptionsCache = {
  [timesheetFieldId: string]: ListTimesheetFieldOptionsResponse.AsObject,
}

export type SetTimesheetFieldOptionsPayload = {
  timesheetFieldId: string,
  response: ListTimesheetFieldOptionsResponse.AsObject,
}

export type State = {
  timesheetFieldOptionsCache: TimesheetFieldOptionsCache,
  timesheetFieldConfigs: TimesheetFieldConfigsState,
  newFieldIds: string[],
  noTimesheetFieldsSiteIdsList: string[],
}

export const initialState: State = {
  timesheetFieldOptionsCache: {},
  timesheetFieldConfigs: {},
  newFieldIds: [],
  noTimesheetFieldsSiteIdsList: [],
}

export type SetTimesheetFieldConfigsPayload = {
  configs: TimesheetFieldConfig.AsObject[],
  siteId: string,
}

type TimesheetFieldOptionPayload = {
  option: TimesheetFieldOption.AsObject,
  timesheetFieldId: string,
}

export type SetDefaultTimesheetFieldOptionId = {
  defaultOptionId: string,
  timesheetFieldId: string,
}

export const slice = createSlice({
  name: "timesheetFields",
  initialState,
  reducers: {
    setTimesheetFieldOptions: (state, action: PayloadAction<SetTimesheetFieldOptionsPayload>) => {
      state.timesheetFieldOptionsCache[action.payload.timesheetFieldId] = {
        ...action.payload.response,
        // Sort options alphabetically by label on initial page load and on refresh.
        timesheetFieldOptionsList: [...action.payload.response.timesheetFieldOptionsList]
          .sort((a, b) => naturalOrderCollator.compare(a.label, b.label)),
      }
    },
    updateTimesheetFieldOption: (state, action: PayloadAction<TimesheetFieldOptionPayload>) => {
      const options: TimesheetFieldOption.AsObject[] = state.timesheetFieldOptionsCache[action.payload.timesheetFieldId]?.timesheetFieldOptionsList || []
      const index = options.findIndex(option => option.id === action.payload.option.id)
      if (index !== -1) {
        options[index] = action.payload.option
      }
    },
    addTimesheetFieldOption: (state, action: PayloadAction<TimesheetFieldOptionPayload>) => {
      const options: TimesheetFieldOption.AsObject[] = state.timesheetFieldOptionsCache[action.payload.timesheetFieldId]?.timesheetFieldOptionsList || []
      options.unshift(action.payload.option)
    },
    deleteTimesheetFieldOption: (state, action: PayloadAction<{ timesheetFieldId: string, optionId: string }>) => {
      const options: TimesheetFieldOption.AsObject[] = state.timesheetFieldOptionsCache[action.payload.timesheetFieldId]?.timesheetFieldOptionsList || []
      const index = options.findIndex(option => option.id === action.payload.optionId)
      if (index !== -1) {
        options.splice(index, 1)
      }
    },
    setDefaultTimesheetFieldOptionId: (state, action: PayloadAction<SetDefaultTimesheetFieldOptionId>) => {
      const timesheetFieldOptionsCache = state.timesheetFieldOptionsCache[action.payload.timesheetFieldId]
      if (timesheetFieldOptionsCache) {
        timesheetFieldOptionsCache.defaultOptionId = action.payload.defaultOptionId
      }
    },
    enableTimesheetFieldOptionsFulfilled: (
      state,
      action: PayloadAction<EnableTimesheetFieldOptionsRequest.AsObject>) => {
      const optionsList = state.timesheetFieldOptionsCache[action.payload.fieldId]?.timesheetFieldOptionsList ?? []
      action.payload.idsList.forEach(optionId => {
        const option = optionsList.find(option => option.id === optionId)
        if (option !== undefined) {
          option.archived = false
        }
      })
    },
    disableTimesheetFieldOptionsFulfilled: (
      state,
      action: PayloadAction<DisableTimesheetFieldOptionsRequest.AsObject>) => {
      const optionsList = state.timesheetFieldOptionsCache[action.payload.fieldId]?.timesheetFieldOptionsList ?? []
      action.payload.idsList.forEach(optionId => {
        const option = optionsList.find(option => option.id === optionId)
        if (option !== undefined) {
          option.archived = true
        }
      })
    },
    setTimesheetFieldConfigs: (state, action: PayloadAction<SetTimesheetFieldConfigsPayload>) => {
      const { configs } = action.payload
      configs.forEach(config => {
        if (config.configRef?.timesheetFieldId) {
          state.timesheetFieldConfigs[config.configRef.timesheetFieldId] = config
        }
      })
      if (configs.length > 0) {
        const index = state.noTimesheetFieldsSiteIdsList.findIndex(siteId => (
          siteId === action.payload.siteId
        ))
        if (index > -1) {
          state.noTimesheetFieldsSiteIdsList.splice(index, 1)
        }
      }
    },
    /**
     * Marks a site as having no timesheet fields available.
     * This prevents unnecessary loading states for sites without fields.
     */
    setNoTimesheetFieldsSiteId: (state, action: PayloadAction<string>) => {
      const index = state.noTimesheetFieldsSiteIdsList.findIndex(id => id === action.payload)
      if (index === -1) {
        state.noTimesheetFieldsSiteIdsList.push(action.payload)
      }
    },
    addNewFieldId: (state, action: PayloadAction<string>) => {
      state.newFieldIds.unshift(action.payload)
    },
  },
})

export const {
  setTimesheetFieldOptions,
  updateTimesheetFieldOption,
  addTimesheetFieldOption,
  deleteTimesheetFieldOption,
  setDefaultTimesheetFieldOptionId,
  enableTimesheetFieldOptionsFulfilled,
  disableTimesheetFieldOptionsFulfilled,
  setTimesheetFieldConfigs,
  addNewFieldId,
  setNoTimesheetFieldsSiteId,
} = slice.actions

export const selectTimesheetFieldConfigs = (state: RootState): TimesheetFieldConfigsState =>
  state.timesheetFields.timesheetFieldConfigs
export const selectTimesheetFieldsAreLoaded = (state: RootState): boolean => {
  const { currentSiteId } = state.userContext
  return currentSiteId !== undefined
    && (
      state.timeline.noTimesheetFieldsSiteIdsList.includes(currentSiteId)
      || Object.keys(state.timeline.timesheetFieldsCache)
        .some(k => currentSiteId && k.startsWith(currentSiteId))
    )
}

export type TimesheetFieldWithConfig = {
  timesheetField: TimesheetField.AsObject,
  config: ListTimesheetFieldOptionsResponse.AsObject | undefined,
}
export const selectTimesheetFieldsWithConfigs = (state: RootState): {
  custom: TimesheetFieldWithConfig[],
  connector: TimesheetFieldWithConfig[],
} => {
  const timesheetFields = Object.values(state.timeline.timesheetFieldsCache)
    .filter(field => field.siteId === state.userContext.currentSiteId)
  const { newFieldIds } = state.timesheetFields
  const timesheetFieldWithConfig = (timesheetField: TimesheetField.AsObject) => ({
    timesheetField,
    config: state.timesheetFields.timesheetFieldOptionsCache[timesheetField.id],
  })
  const sortAlphabetically = (a: TimesheetFieldWithConfig, b: TimesheetFieldWithConfig) =>
    naturalOrderCollator.compare(a.timesheetField.label, b.timesheetField.label)
  // Newly added fields are sorted at the top of the list when user stays on the page.
  // On page refresh they are sorted alphabetically.
  const newFields = newFieldIds
    .map(id => timesheetFields.find(f => f.id === id))
    .filter(notUndefined)
    .map(timesheetFieldWithConfig)
  const customFields = timesheetFields
    .filter(f => f.source === TimesheetFieldSource.USER && !newFieldIds.includes(f.id))
    .map(timesheetFieldWithConfig)
    .sort(sortAlphabetically)
  return ({
    custom: [...newFields, ...customFields],
    connector: timesheetFields
      .filter(f => f.source === TimesheetFieldSource.CONNECTOR)
      .map(timesheetFieldWithConfig)
      .sort(sortAlphabetically),
  })
}

export default slice.reducer
